/** Lettura degli snapshot offline in sola visualizzazione. */

import { SNAPSHOT_STORE, openReviewDatabase } from './reviewDatabase'

export interface Snapshot<T> {
  captured_at: string
  value: T
}

interface SnapshotRow {
  key: string
  captured_at: string
  value: unknown
}

export type SnapshotScope = 'upload-receipt' | 'ingest' | 'export-receipt' | 'inspection-overview'

function key(scope: SnapshotScope, id: string): string {
  return `gridfly.snapshot.${scope}.${id}`
}

function awaited<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((done, fail) => {
    request.onsuccess = () => done(request.result)
    request.onerror = () => fail(request.error ?? new Error('snapshot store error'))
  })
}

function availableFactory(factory?: IDBFactory): IDBFactory {
  const chosen = factory ?? globalThis.indexedDB
  if (!chosen) {
    throw new Error('indexeddb unavailable')
  }
  return chosen
}

async function open(factory?: IDBFactory): Promise<IDBDatabase> {
  return openReviewDatabase([SNAPSHOT_STORE], availableFactory(factory))
}

export async function writeSnapshot<T>(
  scope: SnapshotScope,
  id: string,
  value: T,
  factory?: IDBFactory,
): Promise<void> {
  const db = await open(factory)
  try {
    const tx = db.transaction(SNAPSHOT_STORE, 'readwrite')
    const store = tx.objectStore(SNAPSHOT_STORE)
    await awaited(
      store.put({
        key: key(scope, id),
        captured_at: new Date().toISOString(),
        value,
      } satisfies SnapshotRow),
    )
  } finally {
    db.close()
  }
}

export async function readSnapshot<T>(
  scope: SnapshotScope,
  id: string,
  factory?: IDBFactory,
): Promise<Snapshot<T> | null> {
  const db = await open(factory)
  try {
    const tx = db.transaction(SNAPSHOT_STORE, 'readonly')
    const store = tx.objectStore(SNAPSHOT_STORE)
    const found = (await awaited(store.get(key(scope, id)))) as SnapshotRow | undefined
    if (!found || typeof found.captured_at !== 'string') return null
    return { captured_at: found.captured_at, value: found.value as T }
  } finally {
    db.close()
  }
}
